import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Divider,
  Alert,
  Grid,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  CurrencyRupee as CashIcon,
  QrCode as QrCodeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'

type PaymentMethod = 'card' | 'upi' | 'cash'

const PaymentMethodIcons: Record<PaymentMethod, JSX.Element> = {
  card: <CreditCardIcon />,
  upi: <QrCodeIcon />,
  cash: <CashIcon />,
}

const BookingSuccessPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [bookingDetails, setBookingDetails] = useState<any>(null)

  useEffect(() => {
    if (location.state?.bookingDetails) {
      setBookingDetails(location.state.bookingDetails)
    } else {
      navigate('/rooms')
    }
  }, [location.state, navigate])

  if (!bookingDetails) {
    return null
  }

  const { room, checkIn, checkOut, adults, children, paymentMethod, totalAmount } = bookingDetails as { 
    room: any, 
    checkIn: string, 
    checkOut: string, 
    adults: number, 
    children: number, 
    paymentMethod: PaymentMethod, 
    totalAmount: number 
  }
  const gstAmount = totalAmount * 0.18
  const totalWithGST = totalAmount + gstAmount

  return (
    <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CheckCircleIcon 
            color="success" 
            sx={{ fontSize: 64, mb: 2 }}
          />
          
          <Typography variant="h4" gutterBottom>
            Booking Confirmed!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {paymentMethod === 'cash' ? (
              'Your booking has been confirmed. Please pay at the hotel during check-in.'
            ) : (
              'Your payment has been processed successfully and your booking is confirmed.'
            )}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Booking Details */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Booking Details
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Check-in Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <CalendarIcon fontSize="small" color="primary" />
                      {new Date(checkIn).toLocaleDateString('en-US', { 
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Number of Guests
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <PersonIcon fontSize="small" color="primary" />
                      {adults} Adults{children > 0 ? ` & ${children} Children` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Check-out Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <CalendarIcon fontSize="small" color="primary" />
                      {new Date(checkOut).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      {PaymentMethodIcons[paymentMethod]}
                      {paymentMethod === 'card' ? 'Credit/Debit Card' : 
                       paymentMethod === 'upi' ? 'UPI Payment' : 
                       'Pay at Hotel'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Room Details */}
          {room && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Room Details
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                {room.images?.length > 0 && (
                  <img 
                    src={room.images[0]}
                    alt={room.name}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '16px',
                    }}
                  />
                )}
                <Typography variant="h6">
                  {room.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {room.type}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Payment Summary */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            
            <Stack spacing={2} sx={{ maxWidth: 400, mx: 'auto', mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" color="text.secondary">
                  Room Charges
                </Typography>
                <Typography variant="body1">
                  {convertPrice(totalAmount)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" color="text.secondary">
                  GST (18%)
                </Typography>
                <Typography variant="body1">
                  {convertPrice(gstAmount)}
                </Typography>
              </Box>

              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Total Amount
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {convertPrice(totalWithGST)}
                </Typography>
              </Box>

              {paymentMethod === 'cash' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please pay {convertPrice(totalWithGST)} at the hotel during check-in
                </Alert>
              )}
            </Stack>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/rooms')}
              sx={{
                px: 4,
                py: 1.5,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Back to Rooms
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default BookingSuccessPage 